import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import SupplierCard from '../components/SupplierCard';
import PostRequirementForm from '../components/PostRequirementForm';
import { Button } from '../components/ui/button';
import { useData } from '../context/DataContext';
import { api } from '../lib/api';
import useSEO from '../lib/useSEO';
import { useI18n } from '../context/I18nContext';
import {
  ShieldCheck, TrendingUp, Headphones, Truck, Star, ArrowRight, Quote, Smartphone,
  Factory, Building2, Shirt, Tractor, Stethoscope, Plane, Zap, FlaskConical, Package, Wrench, Tag,
  ChevronLeft, ChevronRight, Sparkles, Loader2,
} from 'lucide-react';

/**
 * Pick a lucide icon based on department-name keywords. We avoid storing icons
 * server-side so admins can rename/add departments without us needing to ship
 * a backend migration. Falls back to a generic Tag icon.
 */
const pickDepartmentIcon = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('industrial')) return Factory;
  if (n.includes('build') || n.includes('real estate') || n.includes('construction')) return Building2;
  if (n.includes('apparel') || n.includes('cloth') || n.includes('garment') || n.includes('lifestyle')) return Shirt;
  if (n.includes('agri') || n.includes('automobile') || n.includes('farm')) return Tractor;
  if (n.includes('hospital') || n.includes('medical') || n.includes('pharma') || n.includes('health')) return Stethoscope;
  if (n.includes('tour') || n.includes('travel')) return Plane;
  if (n.includes('electric') || n.includes('electronic')) return Zap;
  if (n.includes('chemical')) return FlaskConical;
  if (n.includes('transport') || n.includes('logistic') || n.includes('packer') || n.includes('mover')) return Truck;
  if (n.includes('service') || n.includes('repair') || n.includes('maintenance')) return Wrench;
  if (n.includes('packag')) return Package;
  return Tag;
};

const TESTIMONIALS = [
  { id: 't1', name: 'Rajesh Kumar', company: 'Kumar Enterprises, Hyderabad', text: 'TelanganaMart helped me expand my supplier network across Telangana. The quality of leads is consistently high.', rating: 5 },
  { id: 't2', name: 'Priya Sharma', company: 'Sharma Textiles, Warangal', text: 'Got 10+ verified suppliers within a week. The Post Your Requirement feature is a game changer.', rating: 5 },
  { id: 't3', name: 'Anil Mehta', company: 'Mehta Construction, Karimnagar', text: 'We have been buying building materials through TelanganaMart for over 3 years. Trustworthy verified suppliers.', rating: 4 },
];

// Compact Gemini AI search widget that sits in the hero's right column
// (replaces the old "Tell us what you need" requirement card). Submitting
// hands the query off to the dedicated /ai-search results page so the
// full result card (with vendor grid + "Post as Requirement" CTA) has room
// to breathe.
const HERO_EXAMPLES = [
  'GI pipes in Hyderabad',
  '500 kg mild steel angles',
  'PPE kit distributor',
];

const HeroAiSearchCard = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) return;
    setSubmitting(true);
    // Navigate — results page will fire /api/ai/search itself.
    navigate(`/ai-search?q=${encodeURIComponent(query)}`);
  };
  return (
    <div
      className="bg-white dark:bg-[#111827] rounded-lg shadow-2xl p-6 text-gray-900 dark:text-gray-100 max-w-md ml-auto"
      data-testid="hero-ai-search-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-[#ee5e23] text-[10px] font-semibold uppercase tracking-wider border border-orange-200">
          <Sparkles className="w-3 h-3" /> Gemini AI
        </span>
      </div>
      <h3 className="text-lg font-bold text-[#2c54b2] dark:text-orange-400 mb-1">Find vendors by asking</h3>
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
        Type in natural language — we&apos;ll pull the right category and top vendors.
      </p>
      <form onSubmit={submit} className="space-y-3" data-testid="hero-ai-form">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. Who supplies GI pipes in Hyderabad?"
          className="w-full border-2 border-orange-200 dark:border-orange-900/40 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#ee5e23] bg-white dark:bg-[#0b1220]"
          data-testid="hero-ai-input"
          autoComplete="off"
        />
        <Button
          type="submit"
          disabled={submitting || !q.trim()}
          className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white disabled:opacity-60"
          data-testid="hero-ai-submit"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Thinking…</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-2" /> Ask AI</>
          )}
        </Button>
      </form>
      <div className="flex flex-wrap gap-1.5 mt-3" data-testid="hero-ai-examples">
        {HERO_EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            onClick={() => { setQ(ex); navigate(`/ai-search?q=${encodeURIComponent(ex)}`); }}
            className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-[#0b1220] text-gray-600 dark:text-gray-300 hover:bg-orange-100 hover:text-[#ee5e23] border border-gray-200 dark:border-gray-700 transition-colors"
          >
            {ex}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3 text-xs text-gray-600 dark:text-gray-400">
        <ShieldCheck className="w-4 h-4 text-green-600" /> Powered by Gemini · always free
      </div>
    </div>
  );
};

const HERO_HEADLINE_SIZES = { sm: 'text-2xl md:text-4xl', md: 'text-3xl md:text-5xl', lg: 'text-4xl md:text-6xl', xl: 'text-5xl md:text-7xl' };
const HERO_SUBTITLE_SIZES = { sm: 'text-base', md: 'text-lg', lg: 'text-xl' };
const HERO_BADGE_SIZES = { xs: 'text-xs', sm: 'text-sm', md: 'text-base' };
// 'normal' | 'bold' | 'italic' | 'bold-italic' → inline font overrides
const heroTextStyle = (style, color) => ({
  ...(color ? { color } : {}),
  ...(style === 'normal' ? { fontWeight: 400 } : {}),
  ...(style?.includes('bold') ? { fontWeight: 700 } : {}),
  ...(style?.includes('italic') ? { fontStyle: 'italic' } : {}),
});

const HomePage = () => {
  const { tree, suppliers, stats, brands } = useData();
  const { t, lang } = useI18n();
  const [products, setProducts] = useState([]);
  const [postOpen, setPostOpen] = useState(false);
  const [hero, setHero] = useState(null);
  const deptScrollRef = useRef(null);
  const scrollDept = (dir) => {
    const el = deptScrollRef.current;
    if (!el) return;
    // Scroll by ~visible width minus a small overlap so adjacent cards aren't half-hidden.
    const amount = el.clientWidth - 40;
    el.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  // Show up to 15 vendors randomly on the homepage. Memoized on `suppliers.length`
  // so the subset stays stable across re-renders (won't reshuffle on every theme
  // toggle / language switch / scroll-triggered repaint).
  const featuredSuppliers = useMemo(() => {
    if (suppliers.length <= 16) return suppliers;
    const idx = suppliers.map((_, i) => i);
    for (let i = idx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idx[i], idx[j]] = [idx[j], idx[i]];
    }
    return idx.slice(0, 16).map((i) => suppliers[i]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers.length]);

  useSEO({
    title: undefined, // homepage uses default title
    description: "TelanganaMart - Telangana's largest B2B marketplace. 2 Lakh+ verified vendors, 50 Lakh+ products across 56 categories. Get the best quotes from local Telangana suppliers within minutes.",
    keywords: 'B2B marketplace Telangana, Hyderabad suppliers, verified vendors, wholesalers, manufacturers',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'TelanganaMart',
      url: window.location.origin,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${window.location.origin}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  });

  useEffect(() => {
    api.getProducts({ limit: 12 }).then((d) => setProducts(d.items || [])).catch(() => { /* ignore */ });
    api.getHeroSettings().then(setHero).catch(() => { /* keep defaults */ });
  }, []);

  // Admin-configured hero text with language-aware fallback to the built-in translations.
  const hv = (enKey, teKey, fallback) =>
    (lang === 'te' ? (hero?.[teKey] || hero?.[enKey]) : hero?.[enKey]) || fallback;
  const heroBadge = hv('badge_text', 'badge_text_te', t('home.tagline'));
  const heroHeadline = hv('headline', 'headline_te', t('home.headline'));
  const heroSub = hv('subtitle', 'subtitle_te', t('home.sub'));
  const heroCta = hv('cta_text', 'cta_text_te', t('cta.post_requirement'));
  const heroStats = (Array.isArray(hero?.stats) && hero.stats.length > 0)
    ? hero.stats.map((s) => ({
        value: (lang === 'te' && s.value_te) ? s.value_te : s.value,
        label: (lang === 'te' && s.label_te) ? s.label_te : s.label,
      }))
    : stats;

  // Top Categories: only show ones flagged by admin (default True for legacy
  // categories without the field). Cap at 18 — the desktop maximum — and use
  // responsive visibility classes to show 8/12/18 on mobile/tablet/desktop.
  const categories = tree
    .flatMap((m) => m.categories || [])
    .filter((c) => c?.show_in_top !== false)
    .slice(0, 18);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1220] transition-colors">
      <Header />

      <section
        className="bg-gradient-to-br from-[#2c54b2] via-[#2c54b2] to-[#1e3a8a] text-white"
        style={(hero?.bg_from_color || hero?.bg_to_color)
          ? { background: `linear-gradient(to bottom right, ${hero?.bg_from_color || '#2c54b2'}, ${hero?.bg_to_color || '#1e3a8a'})` }
          : undefined}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div
              className={`inline-block px-3 py-1 bg-white/10 backdrop-blur rounded-full font-medium mb-4 border border-white/20 ${HERO_BADGE_SIZES[hero?.badge_size] || 'text-xs'}`}
              style={hero?.badge_color ? { color: hero.badge_color } : undefined}
              data-testid="hero-badge-text"
            >{heroBadge}</div>
            <h1
              className={`${HERO_HEADLINE_SIZES[hero?.headline_size] || 'text-3xl md:text-5xl'} font-bold leading-tight mb-4`}
              style={heroTextStyle(hero?.headline_style, hero?.headline_color)}
              data-testid="hero-headline"
            >{heroHeadline}</h1>
            <p
              className={`text-blue-100 mb-6 ${HERO_SUBTITLE_SIZES[hero?.subtitle_size] || 'text-lg'}`}
              style={heroTextStyle(hero?.subtitle_style, hero?.subtitle_color)}
              data-testid="hero-subtitle"
            >{heroSub}</p>
            {/* Mobile-only Gemini AI hero card — desktop shows the same widget
                in the right column of the two-column grid. Rendered here so it
                sits between the headline and the stat grid on small screens. */}
            <div className="md:hidden mb-6" data-testid="hero-ai-mobile-wrap">
              <HeroAiSearchCard />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="hero-stats-grid">
              {heroStats.map((s, i) => (
                <div key={`${s.label}-${i}`} className="bg-white/10 backdrop-blur border border-white/20 rounded-lg p-3">
                  <div className="text-xl md:text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-blue-100">{s.label}</div>
                </div>
              ))}
            </div>
            <Button
              onClick={() => setPostOpen(true)}
              className="bg-[#ee5e23] hover:bg-[#d24e15] text-white h-12 px-6 text-base font-semibold"
              style={{
                ...(hero?.cta_bg_color ? { backgroundColor: hero.cta_bg_color } : {}),
                ...(hero?.cta_text_color ? { color: hero.cta_text_color } : {}),
              }}
              data-testid="hero-cta-post-requirement"
            >
              {heroCta} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="hidden md:block">
            <HeroAiSearchCard />
          </div>
        </div>
      </section>

      <section className="bg-white dark:bg-[#0b1220] border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {[{ key: 'verified', icon: ShieldCheck, t: t('home.verified_vendors'), d: 'GST verified & trusted' },
            { key: 'quotes', icon: TrendingUp, t: t('home.best_quotes'), d: 'Compare multiple sellers' },
            { key: 'support', icon: Headphones, t: t('home.support'), d: 'Live chat & phone' },
            { key: 'delivery', icon: Truck, t: t('home.delivery'), d: 'Across all 33 districts' }].map((it) => (
            <div key={it.key} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center text-[#ee5e23]"><it.icon className="w-5 h-5" /></div>
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100">{it.t}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{it.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Main categories strip — horizontal slider (6 visible on desktop, fewer on mobile) */}
      {tree.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pt-10">
          <div className="flex items-end justify-between mb-6 gap-3">
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{t('home.shop_by_dept')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Browse our {tree.length} major departments</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => scrollDept('prev')}
                aria-label="Previous categories"
                data-testid="dept-slider-prev"
                className="w-9 h-9 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#111827] text-gray-700 dark:text-gray-200 flex items-center justify-center hover:border-[#ee5e23] hover:text-[#ee5e23] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => scrollDept('next')}
                aria-label="Next categories"
                data-testid="dept-slider-next"
                className="w-9 h-9 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#111827] text-gray-700 dark:text-gray-200 flex items-center justify-center hover:border-[#ee5e23] hover:text-[#ee5e23] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div
            ref={deptScrollRef}
            data-testid="dept-slider-track"
            className="grid grid-flow-col grid-rows-2 auto-cols-[calc((100%-0.75rem)/2)] sm:grid-rows-1 sm:auto-cols-[calc((100%-(0.75rem*2))/3)] md:auto-cols-[calc((100%-(0.75rem*3))/4)] lg:auto-cols-[calc((100%-(0.75rem*5))/6)] gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden -mx-1 px-1 pb-1"
          >
            {tree.map((m) => {
              const Icon = pickDepartmentIcon(m.name);
              return (
                <Link
                  key={m.id}
                  to={`/main/${m.slug}`}
                  className="group bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg p-2 sm:p-3 flex flex-col items-center text-center gap-1.5 sm:flex-row sm:items-center sm:text-left sm:gap-3 hover:border-[#ee5e23] hover:shadow-md transition-all min-w-0 snap-start"
                  data-testid={`dept-card-${m.slug}`}
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-md bg-orange-50 dark:bg-orange-900/20 text-[#ee5e23] flex items-center justify-center group-hover:bg-[#ee5e23] group-hover:text-white transition-colors">
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="min-w-0 w-full sm:flex-1">
                    <div className="text-[11px] leading-tight sm:text-sm font-bold text-gray-900 dark:text-gray-100 sm:leading-tight line-clamp-2 break-words [overflow-wrap:anywhere]">{m.name}</div>
                    <div className="hidden sm:block text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">{(m.categories || []).length} categories</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Top categories grid — 8 cards on mobile, 12 on tablet, 18 on desktop.
          Hidden cards beyond each breakpoint use Tailwind visibility classes so
          the grid stays balanced across viewports. Admins curate which
          categories surface via the `show_in_top` checkbox per category. */}
      {categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-10" data-testid="top-categories-section">
          <div className="flex justify-between items-end mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Top Categories</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Discover products from verified suppliers</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {categories.map((cat, idx) => {
              // idx >= 8  → hide on mobile
              // idx >= 12 → also hide on tablet (md)
              // idx >= 18 → never shown (already sliced above)
              const visClass = idx < 8
                ? ''
                : idx < 12
                  ? 'hidden md:block'
                  : 'hidden lg:block';
              return (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  data-testid={`top-category-card-${idx}`}
                  className={`group bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden hover:shadow-lg hover:border-[#ee5e23] transition-all ${visClass}`}
                >
                  <div className="aspect-square overflow-hidden bg-gray-50 dark:bg-[#0b1220]">
                    <img src={cat.image} alt={cat.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-[#ee5e23] line-clamp-2 transition-colors">{cat.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{cat.products_count}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-end mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Trending Products</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Most enquired products this week</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {products.map((p) => (
            <div key={p.id} className="min-w-0">
              <ProductCard product={p} />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white dark:bg-[#0f172a] py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-end mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Top Verified Vendors</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {suppliers.length > 15
                  ? `Discover 15 verified suppliers (rotating from ${suppliers.length}+ across Telangana)`
                  : 'Connect with Telangana\u2019s most trusted manufacturers'}
              </p>
            </div>
            {suppliers.length > 15 && (
              <Link to="/directory" className="text-sm font-semibold text-[#2c54b2] hover:text-[#ee5e23]">
                View all vendors →
              </Link>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="homepage-vendors-grid">
            {featuredSuppliers.map((s) => (
              <div key={s.id} className="min-w-0">
                <SupplierCard supplier={s} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Brands We Trust</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {brands.map((b) => (
            <div key={b} className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-md p-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200 hover:border-[#ee5e23] hover:text-[#ee5e23] transition-colors">
              {b}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-b from-orange-50 to-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">What Our Buyers Say</h2>
          <p className="text-sm text-gray-600 text-center mb-8">Trusted by lakhs of buyers across Telangana</p>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-6 relative">
                <Quote className="absolute top-4 right-4 w-8 h-8 text-orange-100" />
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={`star-${t.id}-${i}`} className={`w-4 h-4 ${i < t.rating ? 'fill-amber-500 text-amber-500' : 'text-gray-300'}`} />
                  ))}
                </div>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">“{t.text}”</p>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">{t.name}</div>
                  <div className="text-xs text-gray-500">{t.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#2c54b2] text-white">
        <div className="max-w-7xl mx-auto px-4 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center"><Smartphone className="w-7 h-7" /></div>
            <div>
              <h3 className="text-xl font-bold">Download TelanganaMart App</h3>
              <p className="text-sm text-blue-100">Get Telangana&apos;s largest B2B marketplace in your pocket</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="bg-black hover:bg-gray-800 transition-colors px-5 py-2.5 rounded-md text-sm font-medium">Google Play</button>
            <button className="bg-black hover:bg-gray-800 transition-colors px-5 py-2.5 rounded-md text-sm font-medium">App Store</button>
          </div>
        </div>
      </section>

      <Footer />
      <PostRequirementForm open={postOpen} onOpenChange={setPostOpen} />
    </div>
  );
};

export default HomePage;
